import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { uploadWithToast } from "@/lib/queuedIngest";
import { FileSpreadsheet, Upload, Trash2, X, DollarSign, FlaskConical, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import ClientPicker from "@/components/ClientPicker";
import DocumentIdInput, { emptyDocSource } from "@/components/DocumentIdInput";

// SiteCentric superdomain — auto-seeded test client used for QA-mode
// budget ingestion. Production budgets are ingested via the published
// budget-ingest API flow.
const QA_CLIENT_ID = "sitecentric";

function BudgetStatusBadge({ status, totals, meta }) {
    if (status === "processing") {
        return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-300/10 border border-amber-300/30 text-amber-200" data-testid="status-badge-processing">
                <Loader2 className="w-3 h-3 animate-spin" /> AI extracting
            </span>
        );
    }
    if (status === "failed") {
        return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-rose-300/10 border border-rose-300/30 text-rose-200" data-testid="status-badge-failed">
                <AlertTriangle className="w-3 h-3" /> failed
            </span>
        );
    }
    if (status === "ai_unavailable") {
        return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-rose-300/10 border border-rose-300/30 text-rose-200" data-testid="status-badge-ai-unavailable">
                <AlertTriangle className="w-3 h-3" /> AI unavailable — re-extract
            </span>
        );
    }
    if (status === "ready_zero" || (totals?.grand_total_usd ?? 0) === 0) {
        return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-300/10 border border-amber-300/30 text-amber-200" data-testid="status-badge-zero">
                <AlertTriangle className="w-3 h-3" /> $0 — re-extract
            </span>
        );
    }
    const ext = meta?._extractor;
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${
            ext === "ai_agent"
                ? "bg-emerald-300/10 border border-emerald-300/30 text-emerald-200"
                : "bg-sky-300/10 border border-sky-300/30 text-sky-200"
        }`} data-testid="status-badge-ready">
            <CheckCircle2 className="w-3 h-3" /> {ext === "ai_agent" ? "AI" : "fallback"}
        </span>
    );
}

export default function BudgetsPage({ qaMode = false }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);

    const load = async () => {
        try {
            const params = qaMode ? `?client_id=${encodeURIComponent(QA_CLIENT_ID)}` : "";
            const { data } = await api.get(`/admin/study-assets/budgets${params}`);
            const fresh = data.items || [];
            // Preserve any in-flight optimistic placeholders whose upload
            // hasn't surfaced server-side yet (e.g. polling race vs slow
            // multipart upload). They get reaped naturally once the
            // server returns a row with the same filename.
            setItems((prev) => {
                const liveFiles = new Set(fresh.map((b) => b.filename + "|" + (b.client_id || "")));
                const stillOptimistic = prev.filter(
                    (b) => b._optimistic && !liveFiles.has(b.filename + "|" + (b.client_id || "")),
                );
                return [...stillOptimistic, ...fresh];
            });
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    useEffect(() => { setLoading(true); load(); }, [qaMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll while any budget is in 'processing'. AI extraction takes 30-90s.
    useEffect(() => {
        const pending = items.some((b) => b.status === "processing");
        if (!pending) return;
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [items]);

    const openDetail = async (id) => {
        try {
            const { data } = await api.get(`/admin/study-assets/budgets/${id}`);
            setDetail(data);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    const remove = async (b) => {
        if (!confirm(`Delete ${b.filename}?`)) return;        try {
            await api.delete(`/admin/study-assets/budgets/${b.id}`);
            toast.success("Deleted"); load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };

    /** Optimistic insert — puts the in-flight upload at the top of the
     *  list as a 'processing' row so the user sees their file
     *  immediately. The polling loop above then takes over once the
     *  real row arrives from the server. */
    const optimisticInsert = (placeholder) => {
        setItems((prev) => [placeholder, ...prev]);
    };

    return (
        <div className="px-8 py-8" data-testid="budgets-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED]">{qaMode ? "Quality Assurance" : "Clinical Operations"}</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">{qaMode ? "Budget Ingestion (QA)" : "Budgets"}</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        {qaMode ? (
                            <>QA budgets ingested into the SiteCentric test scope — locked to <span className="font-mono text-[#c7a6ff]">client_id=sitecentric</span>.
                            Production budget ingest runs through the published <span className="font-mono">budget-ingest</span> API flow.</>
                        ) : (
                            <>Per-visit procedure pricing extracted from XLSX workbooks or PDF payment schedules by the budget AI agent. Every successful
                            ingest folds its line items into the per-client procedure pricing memory. Budgets are financial documents — IRB approval lives on ICFs (Data Lake → Upload ICF).</>
                        )}
                    </p>
                </div>
                <button
                    data-testid="budgets-ingest-btn"
                    onClick={() => setOpen(true)}
                    className="px-4 py-2.5 rounded-full bg-[#7C3AED] hover:bg-[#9667ee] text-white text-sm font-medium flex items-center gap-2"
                >
                    <Upload className="w-4 h-4" /> Ingest Budget
                </button>
            </div>

            <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="budgets-list">
                {loading ? <div className="p-8 text-white/50">Loading…</div>
                    : items.length === 0 ? (
                        <div className="p-12 text-center text-white/50">
                            <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 opacity-40" />
                            No budgets yet — ingest one to get started.
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-white/[0.02] text-white/50">
                                <tr>
                                    <th className="text-left px-4 py-3 font-normal">File</th>
                                    <th className="text-left px-4 py-3 font-normal">Study</th>
                                    <th className="text-left px-4 py-3 font-normal">Client</th>
                                    <th className="text-right px-4 py-3 font-normal">Visits $</th>
                                    <th className="text-right px-4 py-3 font-normal">Start-up $</th>
                                    <th className="text-right px-4 py-3 font-normal">Grand $</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((b) => (
                                    <tr
                                        key={b.id}
                                        data-testid={`budget-row-${b.id}`}
                                        className={`border-t border-white/5 hover:bg-white/[0.02] ${b.status === "processing" ? "" : "cursor-pointer"}`}
                                        onClick={() => b.status !== "processing" && openDetail(b.id)}
                                    >
                                        <td className="px-4 py-3 font-medium">
                                            <div className="flex items-center gap-2">
                                                <BudgetStatusBadge status={b.status} totals={b.totals} meta={b.metadata} />
                                                <span>{b.filename}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-white/50">{b.metadata?.protocol_number || b.metadata?.study_code || "—"}</td>
                                        <td className="px-4 py-3 text-white/70">{b.client_id}</td>
                                        <td className="px-4 py-3 text-right tabular-nums text-emerald-400">
                                            ${(b.totals?.visits_total_usd || 0).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 text-right tabular-nums text-cyan-400">
                                            ${(b.totals?.start_up_total_usd || 0).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 text-right tabular-nums font-medium">
                                            ${(b.totals?.grand_total_usd || 0).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); remove(b); }}
                                                className="text-white/40 hover:text-rose-400"
                                                data-testid={`budget-delete-${b.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </div>

            {open && (
                <BudgetIngestModal
                    qaMode={qaMode}
                    onClose={() => setOpen(false)}
                    onUploadStart={optimisticInsert}
                    onUploadDone={load}
                />
            )}
            {detail && <BudgetDetailModal budget={detail} onClose={() => setDetail(null)} />}
        </div>
    );
}

function BudgetIngestModal({ qaMode = false, onClose, onUploadStart, onUploadDone }) {
    const [source, setSource] = useState(emptyDocSource("file"));
    const [clientId, setClientId] = useState(qaMode ? QA_CLIENT_ID : "");
    const [siteId, setSiteId] = useState("");
    const [forceReextract, setForceReextract] = useState(false);

    /** Fire-and-forget submit — closes the modal immediately, drops an
     *  optimistic 'processing' row into the parent list, and lets the
     *  POST resolve in the background. The user can navigate elsewhere
     *  while the AI agent extracts in the background. */
    const submit = () => {
        const cid = qaMode ? QA_CLIENT_ID : clientId.trim();
        const fromS3 = source.mode === "document_id";
        const file = source.file;
        const docId = (source.documentId || "").trim();
        if (!cid) { toast.error("client_id required"); return; }
        if (!fromS3 && !file) { toast.error("File required"); return; }
        if (fromS3 && !docId) { toast.error("document_id required"); return; }

        const label = fromS3 ? docId : file.name;
        const sizeBytes = fromS3 ? 0 : file.size;
        const tempId = `temp-${Date.now()}`;
        const placeholder = {
            id: tempId,
            filename: label,
            client_id: cid,
            site_id: siteId.trim() || null,
            file_size_bytes: sizeBytes,
            created_at: new Date().toISOString(),
            status: "processing",
            totals: { grand_total_usd: 0, visits_total_usd: 0, start_up_total_usd: 0 },
            metadata: {},
            _optimistic: true,
        };
        onUploadStart?.(placeholder);

        const fd = new FormData();
        if (fromS3) fd.append("document_id", docId);
        else        fd.append("file", file);
        fd.append("client_id", cid);
        if (siteId.trim()) fd.append("site_id", siteId.trim());
        if (forceReextract) fd.append("force_reextract", "true");

        onClose();

        uploadWithToast(api, `/admin/study-assets/budgets/ingest`, fd, {
            filename: label,
            queuedLabel: forceReextract ? `Re-extracting ${label}…` : `Uploading ${label}…`,
            onDone: () => onUploadDone?.(),
        }).catch(() => {
            onUploadDone?.();
        });
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div
                className="bg-[#0c0c10] border border-white/10 rounded-2xl w-full max-w-lg p-6"
                onMouseDown={(e) => e.stopPropagation()}
                data-testid="budget-ingest-modal"
            >
                <div className="flex items-start justify-between mb-4">
                    <h2 className="font-display text-2xl">Ingest Budget</h2>
                    <button onClick={onClose}><X className="w-5 h-5 text-white/50" /></button>
                </div>
                <div className="space-y-3">
                    <DocumentIdInput
                        value={source}
                        onChange={setSource}
                        accept=".xlsx,.xlsm,.pdf"
                        testId="budget"
                        label="Budget workbook (.xlsx / .xlsm) or PDF payment schedule"
                    />
                    {qaMode ? (
                        <div className="p-2.5 rounded-md border border-[#7C3AED]/30 bg-[#7C3AED]/5 text-xs text-white/70 flex items-center gap-2" data-testid="budget-qa-banner">
                            <FlaskConical className="w-3.5 h-3.5 text-[#c7a6ff]" />
                            Locked to <code className="text-[#c7a6ff] mx-1">client_id=sitecentric</code> (test scope).
                        </div>
                    ) : (
                        <ClientPicker
                            value={clientId} onChange={setClientId}
                            label="Client" required excludeDisabled
                            testId="budget-client-id"
                        />
                    )}
                    <label className="flex items-start gap-2.5 p-2.5 rounded-md border border-white/10 bg-white/[0.02] cursor-pointer hover:bg-white/[0.04]" data-testid="budget-force-reextract-toggle">
                        <input
                            type="checkbox" checked={forceReextract}
                            onChange={(e) => setForceReextract(e.target.checked)}
                            data-testid="budget-force-reextract"
                            className="mt-0.5 accent-[#7C3AED]"
                        />
                        <div>
                            <div className="text-xs text-white/70">Re-extract if already ingested</div>
                            <div className="text-[10px] text-white/40 mt-0.5">
                                Bypass the dedupe cache and re-run the budget extractor on the same file.
                                Useful if a previous ingest produced empty totals.
                            </div>
                        </div>
                    </label>
                    <input
                        placeholder="site_id (optional)" value={siteId}
                        data-testid="budget-site-id"
                        onChange={(e) => setSiteId(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"
                    />
                </div>
                <p className="text-xs text-white/40 mt-2">
                    💡 This UI is for testing. In production, budget ingestion happens via the
                    <code className="px-1 bg-white/5 rounded mx-1">budget-ingest</code> published flow.
                </p>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-white/60">Cancel</button>
                    <button
                        onClick={submit}
                        data-testid="budget-submit-btn"
                        className="px-4 py-2 rounded-full bg-[#7C3AED] text-white text-sm"
                    >
                        Ingest
                    </button>
                </div>
                <p className="text-xs text-white/40 mt-3">
                    Extraction continues in the background after upload — you can close this dialog and keep working.
                </p>
            </div>
        </div>
    );
}

function BudgetDetailModal({ budget, onClose }) {
    const p = budget.payload || {};
    const [tab, setTab] = useState("visits");
    const [protocols, setProtocols] = useState([]);
    const [protocolId, setProtocolId] = useState("");
    const [analysis, setAnalysis] = useState({});
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/admin/protocols`);
                setProtocols((data.items || []).filter((x) => x.company_id === budget.client_id));
            } catch { /* ignore */ }
        })();
    }, [budget.client_id]);

    const runAlign = async () => {
        if (!protocolId) { toast.error("Pick a protocol first"); return; }
        setBusy("align");
        try {
            const { data } = await api.post(`/admin/study-analysis/budgets/${budget.id}/align/${protocolId}`);
            setAnalysis((a) => ({ ...a, align: data })); setTab("align");
            toast.success(`Alignment: ${data.summary?.matched || 0} matched, ${data.summary?.unpaid || 0} unpaid`);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setBusy(null); }
    };

    const runFeasibility = async () => {
        setBusy("feasibility");
        try {
            const { data } = await api.post(`/admin/study-analysis/budgets/${budget.id}/feasibility`);
            setAnalysis((a) => ({ ...a, feasibility: data })); setTab("feasibility");
            toast.success(`Feasibility: ${data.summary?.flagged || 0} recommendations`);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setBusy(null); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onMouseDown={onClose}>
            <div
                className="bg-[#0c0c10] border border-white/10 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-auto p-6"
                onMouseDown={(e) => e.stopPropagation()}
                data-testid="budget-detail-modal"
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h2 className="font-display text-2xl">{budget.filename}</h2>
                        <p className="text-xs text-white/40 mt-1">
                            {p.metadata?.study_code} · {p.metadata?.indication} · {budget.client_id}
                        </p>
                    </div>
                    <button onClick={onClose}><X className="w-5 h-5 text-white/50" /></button>
                </div>

                <div className="flex items-center gap-2 mb-4">
                    <select
                        value={protocolId} onChange={(e) => setProtocolId(e.target.value)}
                        data-testid="budget-protocol-select"
                        className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs"
                    >
                        <option value="">Pick protocol for alignment…</option>
                        {protocols.map((pr) => (
                            <option key={pr.id} value={pr.id}>
                                {pr.protocol_number || "?"} v{pr.version || "?"} — {(pr.title || "").slice(0, 50)}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={runAlign} disabled={busy || !protocolId}
                        data-testid="budget-align-btn"
                        className="px-3 py-1.5 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 text-xs hover:bg-cyan-400/20 disabled:opacity-50"
                    >{busy === "align" ? "…" : "Align with protocol"}</button>
                    <button
                        onClick={runFeasibility} disabled={busy}
                        data-testid="budget-feasibility-btn"
                        className="px-3 py-1.5 rounded-full bg-violet-400/10 border border-violet-400/30 text-violet-300 text-xs hover:bg-violet-400/20 disabled:opacity-50"
                    >{busy === "feasibility" ? "…" : "Feasibility check"}</button>
                </div>

                <div className="grid grid-cols-4 gap-3 mb-5">
                    <Stat label="Visits Total" value={p.totals?.visits_total_usd} accent="emerald" />
                    <Stat label="Ancillary Total" value={p.totals?.ancillary_total_usd} accent="cyan" />
                    <Stat label="Start-up Total" value={p.totals?.start_up_total_usd} accent="amber" />
                    <Stat label="Grand Total" value={p.totals?.grand_total_usd} accent="white" big />
                </div>

                <div className="flex gap-2 border-b border-white/10 mb-3">
                    {[
                        ["visits", `Visit Procedures (${(p.visit_procedures || []).length})`],
                        ["ancillary", `Ancillary (${(p.ancillary || []).length})`],
                        ["additional", `Additional Procedures (${(p.additional_procedures || []).length})`],
                        ["startup", `Start-up Costs (${(p.start_up_costs || []).length})`],
                        ["align", `Alignment${analysis.align ? ` (${analysis.align.summary?.matched || 0}/${analysis.align.summary?.total_soe_entries || 0})` : ""}`],
                        ["feasibility", `Feasibility${analysis.feasibility ? ` (${analysis.feasibility.summary?.flagged || 0})` : ""}`],
                    ].map(([k, l]) => (
                        <button
                            key={k} onClick={() => setTab(k)}
                            data-testid={`budget-tab-${k}`}
                            className={`px-3 py-2 text-sm ${tab === k ? "text-white border-b-2 border-[#7C3AED]" : "text-white/40 hover:text-white/70"}`}
                        >{l}</button>
                    ))}
                </div>

                {tab === "visits" && <VisitProceduresTable rows={p.visit_procedures || []} visits={p.visits || []} />}
                {tab === "ancillary" && <SimpleTable rows={p.ancillary || []} cols={["code", "description", "unit", "total"]} />}
                {tab === "additional" && <SimpleTable rows={p.additional_procedures || []} cols={["code", "procedure", "invoiceable", "unit_cost"]} />}
                {tab === "startup" && <SimpleTable rows={p.start_up_costs || []} cols={["code", "description", "amount"]} />}
                {tab === "align" && <AlignmentResult result={analysis.align} />}
                {tab === "feasibility" && <BudgetFeasibilityResult result={analysis.feasibility} />}
            </div>
        </div>
    );
}

function AlignmentResult({ result }) {
    if (!result) return <div className="text-white/40 text-sm py-4">Pick a protocol and click "Align" to run.</div>;
    return (
        <div data-testid="budget-alignment-result">
            <div className="grid grid-cols-4 gap-2 mb-3">
                <Stat label="SoE entries" value={result.summary?.total_soe_entries} accent="white" />
                <Stat label="Matched" value={result.summary?.matched} accent="emerald" />
                <Stat label="Unpaid" value={result.summary?.unpaid} accent="rose" />
                <Stat label="Extras" value={result.summary?.extra} accent="amber" />
            </div>

            {(result.unpaid_protocol_procedures || []).length > 0 && (
                <Section title={`UNPAID protocol procedures (${result.unpaid_protocol_procedures.length})`}>
                    <table className="text-xs w-full">
                        <tbody>
                            {result.unpaid_protocol_procedures.map((u, i) => (
                                <tr key={i} className="border-t border-white/5">
                                    <td className="px-2 py-1.5 text-rose-400 font-mono">{u.visit}</td>
                                    <td className="px-2 py-1.5">{u.procedure}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Section>
            )}

            {(result.invoice_only_procedures || []).length > 0 && (
                <Section title={`Invoice-only procedures (${result.invoice_only_procedures.length})`}>
                    <table className="text-xs w-full">
                        <tbody>
                            {result.invoice_only_procedures.map((u, i) => (
                                <tr key={i} className="border-t border-white/5">
                                    <td className="px-2 py-1.5 text-amber-400 font-mono">{u.visit}</td>
                                    <td className="px-2 py-1.5">{u.procedure}</td>
                                    <td className="px-2 py-1.5 text-white/40">{u.code}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Section>
            )}

            {(result.extra_budget_procedures || []).length > 0 && (
                <Section title={`Budget procedures NOT in protocol (${result.extra_budget_procedures.length})`}>
                    <table className="text-xs w-full">
                        <tbody>
                            {result.extra_budget_procedures.map((u, i) => (
                                <tr key={i} className="border-t border-white/5">
                                    <td className="px-2 py-1.5 font-mono text-white/60">{u.code}</td>
                                    <td className="px-2 py-1.5">{u.procedure}</td>
                                    <td className="px-2 py-1.5 text-emerald-400 tabular-nums text-right">${(u.paid_usd || 0).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Section>
            )}
        </div>
    );
}

function BudgetFeasibilityResult({ result }) {
    if (!result) return <div className="text-white/40 text-sm py-4">Click "Feasibility check" to run.</div>;
    const recs = result.recommendations || [];
    return (
        <div data-testid="budget-feasibility-result">
            <div className="grid grid-cols-4 gap-2 mb-3">
                <Stat label="Priced" value={result.summary?.total_priced_procedures} accent="white" />
                <Stat label="Flagged" value={result.summary?.flagged} accent="amber" />
                <Stat label="Below median" value={result.summary?.below_median} accent="rose" />
                <Stat label="Above median" value={result.summary?.above_median} accent="emerald" />
            </div>
            <div className="space-y-2">
                {recs.map((r, i) => (
                    <div key={i} className={`border rounded-lg p-3 text-sm ${
                        r.severity === "warning" ? "border-amber-400/30 bg-amber-400/5" : "border-white/10 bg-white/[0.02]"
                    }`}>
                        <div className="flex items-center justify-between">
                            <span className="font-medium">{r.procedure_code} · {r.procedure}</span>
                            <span className={`text-xs tabular-nums ${r.delta_percent < 0 ? "text-rose-400" : "text-emerald-400"}`}>
                                {r.delta_percent > 0 ? "+" : ""}{r.delta_percent}%
                            </span>
                        </div>
                        <p className="text-xs text-white/60 mt-1">{r.recommendation}</p>
                        <div className="text-xs text-white/40 mt-1">
                            current ${r.current_amount_usd?.toLocaleString()} · median ${r.history_median_usd?.toLocaleString()} · n={r.occurrences} ({r.confidence})
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mb-5">
            <div className="text-xs uppercase tracking-wider text-white/40 mb-2">{title}</div>
            {children}
        </div>
    );
}

function Stat({ label, value, accent = "white", big = false }) {
    const colors = { emerald: "text-emerald-400", cyan: "text-cyan-400", amber: "text-amber-400", white: "text-white" };
    return (
        <div className={`rounded-xl border border-white/10 p-3 ${big ? "bg-white/[0.04]" : "bg-white/[0.02]"}`}>
            <div className="text-xs text-white/50">{label}</div>
            <div className={`mt-1 ${big ? "text-2xl" : "text-xl"} font-medium tabular-nums ${colors[accent]}`}>
                ${(value || 0).toLocaleString()}
            </div>
        </div>
    );
}

function VisitProceduresTable({ rows, visits }) {
    if (rows.length === 0) return <div className="text-white/50 text-sm py-6">No visit procedures.</div>;
    return (
        <div className="overflow-auto">
            <table className="text-xs w-full">
                <thead className="text-white/40 sticky top-0 bg-[#0c0c10]">
                    <tr>
                        <th className="text-left px-2 py-2 font-normal">Code</th>
                        <th className="text-left px-2 py-2 font-normal">Procedure</th>
                        <th className="text-right px-2 py-2 font-normal">Total</th>
                        {visits.map((v) => (
                            <th key={v.label} className="text-right px-2 py-2 font-normal">{v.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className="border-t border-white/5 hover:bg-white/[0.02]">
                            <td className="px-2 py-1.5 text-white/60">{r.code}</td>
                            <td className="px-2 py-1.5 max-w-md truncate" title={r.procedure}>{r.procedure}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-emerald-400">{r.total != null ? `$${r.total}` : "—"}</td>
                            {visits.map((v) => {
                                const c = r.per_visit?.[v.label];
                                return (
                                    <td key={v.label} className="px-2 py-1.5 text-right tabular-nums text-white/70">
                                        {c?.amount != null ? `$${c.amount}` : c?.marker === "invoice" ? <span className="text-amber-400">inv</span> : ""}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SimpleTable({ rows, cols }) {
    if (rows.length === 0) return <div className="text-white/50 text-sm py-6">No rows.</div>;
    return (
        <div className="overflow-auto">
            <table className="text-xs w-full">
                <thead className="text-white/40 sticky top-0 bg-[#0c0c10]">
                    <tr>{cols.map((c) => <th key={c} className="text-left px-2 py-2 font-normal capitalize">{c.replace(/_/g, " ")}</th>)}</tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className="border-t border-white/5 hover:bg-white/[0.02]">
                            {cols.map((c) => (
                                <td key={c} className="px-2 py-1.5">
                                    {typeof r[c] === "number" ? <span className="text-emerald-400 tabular-nums">${r[c].toLocaleString()}</span>
                                        : typeof r[c] === "boolean" ? (r[c] ? "yes" : "no")
                                            : (r[c] ?? "—")}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
